import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthPaidMediaTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { buildCommsPurposeOptions } from '../growth-comms.models';
import { GrowthCrmService, GrowthSegmentResponse } from '../growth-crm.service';
import {
  GrowthPaidMediaAccountDto,
  GrowthPaidMediaAudienceSyncDto,
  accountDisplayLabel
} from './growth-paid-media.models';
import { GrowthPaidMediaService } from './growth-paid-media.service';

@Component({
  standalone: true,
  selector: 'ain-growth-paid-media-audience-sync',
  imports: [
    AinPageComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    RouterLink
  ],
  template: `
    <ain-page [title]="t('growth.paidMedia.audienceSync.title')" [description]="t('growth.paidMedia.audienceSync.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.paidMedia.audienceSync.help.title'"
        [descriptionKey]="'growth.paidMedia.audienceSync.help.description'"
        [bulletKeys]="['growth.paidMedia.audienceSync.help.bullets.consent', 'growth.paidMedia.audienceSync.help.bullets.live']" />

      <section class="card">
        <div class="toolbar">
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
          <a class="link-btn" routerLink="/growth/paid-media/accounts">{{ t('growth.paidMedia.audienceSync.openAccounts') }}</a>
        </div>
        <div class="grid">
          <ain-form-field [label]="t('growth.paidMedia.audienceSync.fields.account')">
            <p-select
              [options]="accountOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="form.accountId"
              [filter]="true"
              appendTo="body"
              [placeholder]="t('growth.paidMedia.audienceSync.fields.accountPlaceholder')" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.paidMedia.audienceSync.fields.segment')">
            <p-select
              [options]="segmentOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="form.segmentId"
              [filter]="true"
              appendTo="body"
              [placeholder]="t('growth.paidMedia.audienceSync.fields.segmentPlaceholder')" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.paidMedia.audienceSync.fields.consentPurpose')">
            <p-select
              [options]="purposeOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="form.consentPurposeKey"
              appendTo="body" />
          </ain-form-field>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.paidMedia.audienceSync.actions.triggerSync')"
            [loading]="syncing()"
            (click)="triggerSync()"></button>
        </div>
      </section>

      @if (lastSync()) {
        <section class="card">
          <h3>{{ t('growth.paidMedia.audienceSync.sections.status') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.paidMedia.audienceSync.help.status.title'"
            [descriptionKey]="'growth.paidMedia.audienceSync.help.status.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.paidMedia.audienceSync.fields.account') }}</span>
              <input pInputText type="text" [ngModel]="accountLabel(lastSync()!.accountId)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.audienceSync.fields.segment') }}</span>
              <input pInputText type="text" [ngModel]="segmentLabel(lastSync()!.segmentId)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.audienceSync.fields.consentPurpose') }}</span>
              <input pInputText type="text" [ngModel]="purposeLabel(lastSync()!.consentPurposeKey)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.audienceSync.columns.status') }}</span>
              <input pInputText type="text" [ngModel]="syncStatusLabel(lastSync()!.status)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.audienceSync.columns.hashedMemberCount') }}</span>
              <input pInputText type="text" [ngModel]="lastSync()!.hashedMemberCount" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.audienceSync.columns.externalAudienceId') }}</span>
              <input pInputText type="text" [ngModel]="lastSync()!.externalAudienceId || '-'" [disabled]="true" />
            </label>
          </div>
        </section>
      }

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .link-btn { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .message.error { color: #b91c1c; }
    .message.success { color: #15803d; }
    @media (max-width: 960px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthPaidMediaAudienceSyncComponent implements OnInit {
  private readonly api = inject(GrowthPaidMediaService);
  private readonly crm = inject(GrowthCrmService);
  private readonly localization = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly accounts = signal<GrowthPaidMediaAccountDto[]>([]);
  readonly segments = signal<GrowthSegmentResponse[]>([]);
  readonly lastSync = signal<GrowthPaidMediaAudienceSyncDto | null>(null);
  readonly syncing = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');

  form = {
    accountId: null as string | null,
    segmentId: null as string | null,
    consentPurposeKey: 'marketing'
  };

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthPaidMediaChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Status board is command-driven; keep connection live for operator awareness.
      });

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadLookups();
  }

  accountOptions(): Array<{ label: string; value: string }> {
    return this.api.accountOptions(this.accounts());
  }

  segmentOptions(): Array<{ label: string; value: string }> {
    return this.segments().map(item => ({
      value: item.id,
      label: item.name || item.key
    }));
  }

  purposeOptions(): Array<{ label: string; value: string }> {
    return buildCommsPurposeOptions(key => this.t(key));
  }

  accountLabel(accountId: string): string {
    const account = this.accounts().find(item => item.id === accountId);
    return account ? accountDisplayLabel(account) : this.t('growth.paidMedia.common.unknownAccount');
  }

  segmentLabel(segmentId: string): string {
    const segment = this.segments().find(item => item.id === segmentId);
    return segment?.name || segment?.key || this.t('growth.paidMedia.common.unknownSegment');
  }

  purposeLabel(purposeKey: string): string {
    return this.t(`growth.comms.purpose.${purposeKey}`);
  }

  syncStatusLabel(status: string): string {
    return this.t(`growth.paidMedia.syncStatus.${status}`);
  }

  loadLookups(): void {
    this.api.listAccounts().subscribe({
      next: rows => {
        this.accounts.set(rows);
        if (!this.form.accountId) {
          this.form.accountId = rows.find(item => item.isEnabled)?.id ?? rows[0]?.id ?? null;
        }
      },
      error: () => this.accounts.set([])
    });
    this.crm.listSegments().subscribe({
      next: rows => {
        this.segments.set(rows);
        if (!this.form.segmentId) {
          this.form.segmentId = rows[0]?.id ?? null;
        }
      },
      error: () => this.segments.set([])
    });
  }

  triggerSync(): void {
    if (!this.form.accountId || !this.form.segmentId) {
      this.errorMessage.set(this.t('growth.paidMedia.audienceSync.messages.validationFailed'));
      return;
    }
    this.syncing.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.syncAudience({
      accountId: this.form.accountId,
      segmentId: this.form.segmentId,
      consentPurposeKey: this.form.consentPurposeKey,
      idempotencyKey: crypto.randomUUID()
    }).pipe(finalize(() => this.syncing.set(false))).subscribe({
      next: result => {
        this.lastSync.set(result);
        this.successMessage.set(this.t('growth.paidMedia.audienceSync.messages.syncSucceeded'));
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.audienceSync.messages.syncFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthPaidMediaTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
